import { ApplicationConfig, provideZoneChangeDetection, APP_INITIALIZER, isDevMode } from '@angular/core';
import { environment } from '../environments/environment';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptors } from '@angular/common/http';
import { authInterceptor } from '@shared/auth/auth.interceptor';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideOAuthClient } from 'angular-oauth2-oidc';
import { providePrimeNG } from 'primeng/config';
import Aura from '@primeng/themes/aura';
import { updatePreset } from '@primeuix/themes';
import { ApplicationConfigService, CADRICIEL_CORE_CONFIG } from '@cadriciel/core';
import { LayoutService } from '@shared/layouts/cerema-layout/service/layout.service';
import { APP_LAYOUT_CONFIG } from './app.layout';
import { provideTransloco } from '@jsverse/transloco';
import { TranslocoHttpLoader } from './transloco-loader';
import { provideEchartsCore } from 'ngx-echarts';
import * as echarts from 'echarts/core';

import { routes } from './app.routes';

// Helper function to get the primary color preset extension
function getPrimaryColorPreset(colorName: string) {
  const palette = (Aura as any).primitive?.[colorName];
  if (!palette) return {};

  return {
    semantic: {
      primary: palette,
      colorScheme: {
        light: {
          primary: {
            color: '{primary.500}',
            contrastColor: '#ffffff',
            hoverColor: '{primary.600}',
            activeColor: '{primary.700}'
          },
          highlight: {
            background: '{primary.50}',
            focusBackground: '{primary.100}',
            color: '{primary.700}',
            focusColor: '{primary.800}'
          }
        },
        dark: {
          primary: {
            color: '{primary.400}',
            contrastColor: '{surface.900}',
            hoverColor: '{primary.300}',
            activeColor: '{primary.200}'
          },
          highlight: {
            background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
            focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
            color: 'rgba(255,255,255,.87)',
            focusColor: 'rgba(255,255,255,.87)'
          }
        }
      }
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    { provide: CADRICIEL_CORE_CONFIG, useValue: environment },
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptors([authInterceptor])),
    provideAnimations(),
    provideOAuthClient(),
    provideTransloco({
      config: {
        availableLangs: ['fr', 'en'],
        defaultLang: 'fr',
        // Remove this option if your application doesn't support changing language in runtime.
        reRenderOnLangChange: true,
        prodMode: !isDevMode(),
      },
      loader: TranslocoHttpLoader
    }),
    provideEchartsCore({ echarts }),
    {
      provide: APP_INITIALIZER,
      useFactory: (configService: ApplicationConfigService, layoutService: LayoutService) => () => {
        console.log('AppConfig: Initializing...');
        (configService as any).endpointPrefix = (environment as any).serverApiUrl;
        (configService as any).homePath = (environment as any).homePath || '/';
        (configService as any).authenticatedHomePath = (environment as any).redirectPage || '/app';
        layoutService.layoutConfig.update((config) => ({ ...config, ...(APP_LAYOUT_CONFIG as any) }));

        // Apply the primary color theme at startup
        const primaryColor = APP_LAYOUT_CONFIG.primary || 'blue';
        const preset = getPrimaryColorPreset(primaryColor);
        if (preset.semantic) {
          updatePreset(preset);
          console.log('AppConfig: Applied primary color:', primaryColor);
        }
      },
      deps: [ApplicationConfigService, LayoutService],
      multi: true
    },
    /*{
      provide: APP_INITIALIZER,
      useFactory: (registry: ComponentRegistryService) => () => {
        // Import widget dynamically to avoid circular dependencies
        import('./form-widgets').then(({ TextFieldWidgetComponent }) => {
          registry.register({
            type: 'text-field',
            label: 'Text Field',
            icon: 'pi pi-pencil',
            category: 'Input',
            componentType: TextFieldWidgetComponent,
            defaultProperties: {
              label: 'Text Field',
              placeholder: 'Enter text...',
              type: 'text'
            }
          });
          console.log('Form widgets registered');
        });
      },
      deps: [ComponentRegistryService],
      multi: true
    },*/
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.app-dark',
        }
      }
    })
  ]
};
